import { FC, useContext, useEffect, useMemo, useState, useCallback } from "react";
import moment from "moment";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { ProjectionProviderByEnumToDisplay } from "@app/projects/servicing-provider/utility";
import ProjectionProviderByEnum from "@app/projects/servicing-provider/projectionProviderByEnum";
import { ALL_OPTIONS_TEXT } from "@app/projects/network-assessment/provider-actions/ProviderActionsDisplayFunctions";
import { UserContext } from "@app/user/UserContext";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { capitalizeFirstLetter } from "@app/shared/utility/capitalizeFirstLetter";
import Roles from "@app/user/roles";
import { ProcessStatusEnum, ProviderActionsTabsEnum } from "@app/projects/network-assessment/provider-actions/enumerators";
import { ProviderActionsFiltersTabProps } from "@app/projects/network-assessment/provider-actions/interfaces";

const ProviderActionsFilters: FC<ProviderActionsFiltersTabProps> = (props: ProviderActionsFiltersTabProps) => {
  const userContext = useContext(UserContext);

  const [validFromDate, setValidFromDate] = useState(true);
  const [validToDate, setValidToDate] = useState(true);

  const searchButtonDisabled = useMemo(() => {
    return props.filteringCriteriaFailedToLoad || !validFromDate || !validToDate;
  }, [props.filteringCriteriaFailedToLoad, validFromDate, validToDate]);

  const clearAllDisabled = useMemo(() => props.filteringCriteriaFailedToLoad, [props.filteringCriteriaFailedToLoad]);

  const getCustomSelectInputText = useCallback((selectedCount: number, totalCount: number): string => {
    if (selectedCount <= 0 || totalCount <= 0) {
      return ALL_OPTIONS_TEXT;
    }

    if (selectedCount >= totalCount) {
      return ALL_OPTIONS_TEXT;
    }

    return `${selectedCount} of ${totalCount} item(s) selected`;
  }, []);

  const specialtySelectionText = useMemo(() => {
    return getCustomSelectInputText(
      props.tabState.filters.specialtyGroupCodes.length,
      props.specialtyOptions.length
    );
  }, [getCustomSelectInputText, props.specialtyOptions.length, props.tabState.filters.specialtyGroupCodes.length]);

  const reasonSelectionText = useMemo(() => {
    return getCustomSelectInputText(
      props.tabState.filters.reason.length,
      props.reasonOptions.length
    );
  }, [getCustomSelectInputText, props.reasonOptions.length, props.tabState.filters.reason.length]);

  const recordingCriteriaSelectionText = useMemo(() => {
    return getCustomSelectInputText(
      props.tabState.filters.recordingCriteria.length,
      props.recordingCriteriaOptions.length
    );
  }, [getCustomSelectInputText, props.recordingCriteriaOptions.length, props.tabState.filters.recordingCriteria.length]);

  useEffect(() => {
    const dateFrom = props.tabState.filters.actionDateFrom;
    const dateTo = props.tabState.filters.actionDateTo;

    if (!dateFrom || !dateTo) {
      setValidFromDate(true);
      setValidToDate(true);
      return;
    }

    const datesAreValid = dateFrom <= dateTo;
    setValidFromDate(datesAreValid);
    setValidToDate(datesAreValid);
  }, [props.tabState.filters.actionDateFrom, props.tabState.filters.actionDateTo]);

  return (
    <>
      <div className="provider-actions__filters-container">
        <QESSelect
          allowEmpty={false}
          className="item-column"
          testId="provider-actions__search-by-select"
          label="Search by"
          onChange={(e) =>
            props.setTabState((prev) => ({
              ...prev,
              filters: {
                ...prev.filters,
                searchByOption: e.target.value as ProjectionProviderByEnum,
              },
            }))
          }
          value={props.tabState.filters.searchByOption}
        >
          {Object.values(ProjectionProviderByEnum)
            .filter((option) =>
              option !== ProjectionProviderByEnum.NPIAndTIN &&
              option !== ProjectionProviderByEnum.ContractEntity &&
              (option !== ProjectionProviderByEnum.TIN || userContext.roles.includes(Roles.TIN_VIEWER))
            )
            .map((option, optionIndex) => (
              <option key={optionIndex} value={option}>
                {ProjectionProviderByEnumToDisplay(option)}
              </option>
            ))}
        </QESSelect>

        <div className="item-column search-input-container">
          <QESTextInput
            testId="provider-actions__search-by-input"
            label={""}
            inputAppend={<QesIcon iconName={QesIcons.SearchSm} description="Search" />}
            onChange={(e) =>
              props.setTabState((prev) => ({
                ...prev,
                filters: {
                  ...prev.filters,
                  searchByInput: e.target.value,
                },
              }))
            }
            value={props.tabState.filters.searchByInput}
          />
        </div>

        <MultiselectDropdown
          containerClassName="item-column"
          customWidth={true}
          customSelectInputText={specialtySelectionText}
          items={props.specialtyOptions}
          label="Specialty"
          onSelectionChanged={(selectedItems) =>
            props.setTabState((prev) => ({
              ...prev,
              filters: {
                ...prev.filters,
                specialtyGroupCodes: selectedItems.map((x) => x.key),
              },
            }))
          }
          selectedItems={props.tabState.filters.specialtyGroupCodes.map((key) => ({ key, name: key }))}
          testId="provider-actions__specialties-multi-select-dropdown"
          showTooltipOnOverflow={true}
        />

        <MultiselectDropdown
          containerClassName="item-column"
          customWidth={true}
          customSelectInputText={reasonSelectionText}
          items={props.reasonOptions}
          label="Reason"
          onSelectionChanged={(selectedItems) =>
            props.setTabState((prev) => ({
              ...prev,
              filters: {
                ...prev.filters,
                reason: selectedItems.map((x) => x.key),
              },
            }))
          }
          selectedItems={props.tabState.filters.reason.map((key) => ({ key, name: key }))}
          testId="provider-actions__reasons-multi-select-dropdown"
          showTooltipOnOverflow={true}
        />

        <MultiselectDropdown
          containerClassName="item-column"
          customWidth={true}
          customSelectInputText={recordingCriteriaSelectionText}
          items={props.recordingCriteriaOptions}
          label="Recording Criteria"
          onSelectionChanged={(selectedItems) =>
            props.setTabState((prev) => ({
              ...prev,
              filters: {
                ...prev.filters,
                recordingCriteria: selectedItems.map((x) => x.key),
              },
            }))
          }
          selectedItems={props.tabState.filters.recordingCriteria.map((key) => ({ key, name: key }))}
          testId="provider-actions__recording-criteria-multi-select-dropdown"
          showTooltipOnOverflow={true}
        />

        <QESDateInput
          containerClassName="item-column"
          isValid={validFromDate}
          label="Action Date From"
          testId="provider-actions__from-date"
          onChange={(e) =>
            props.setTabState((prev) => ({
              ...prev,
              filters: {
                ...prev.filters,
                actionDateFrom: e.target.value ? moment(e.target.value).toDate() : null,
              },
            }))
          }
          unrequiredValidation={true}
          value={props.tabState.filters.actionDateFrom ? moment(props.tabState.filters.actionDateFrom).format("YYYY-MM-DD") : ""}
        />

        <QESDateInput
          containerClassName="item-column"
          isValid={validToDate}
          label="Action Date To"
          testId="provider-actions__to-date"
          onChange={(e) =>
            props.setTabState((prev) => ({
              ...prev,
              filters: {
                ...prev.filters,
                actionDateTo: e.target.value ? moment(e.target.value).toDate() : null,
              },
            }))
          }
          unrequiredValidation={true}
          value={props.tabState.filters.actionDateTo ? moment(props.tabState.filters.actionDateTo).format("YYYY-MM-DD") : ""}
        />

        <QESButton
          className="item-column action-buttons"
          disabled={clearAllDisabled}
          qesType="secondary"
          qesTestId="provider-actions__clear-all-button"
          onClick={props.onClearAll}
        >
          Clear All
        </QESButton>

        <QESButton
          className="item-column action-buttons"
          disabled={searchButtonDisabled}
          id="provider-actions__search-button"
          qesType="secondary"
          qesTestId="provider-actions__search-button"
          onClick={props.onSearch}
        >
          Search
        </QESButton>
      </div>

      <div className="d-flex justify-content-between">
        <div className="provider-actions__filters-container">
          <label className="qes-text-input__label">Process Status:</label>
          <QESSelect
            allowEmpty={false}
            className="item-column provider-actions__process-select-status-container"
            containerClassName="provider-actions__process-select-status-container"
            testId="provider-actions__process-status-select"
            hideLabel={true}
            label=""
            onChange={(e) =>
              props.setTabState((prev) => ({
                ...prev,
                filters: {
                  ...prev.filters,
                  processStatus: e.target.value as ProcessStatusEnum,
                },
              }))
            }
            value={props.tabState.filters.processStatus}
          >
            {Object.keys(ProcessStatusEnum).map((key) => {
              const value = ProcessStatusEnum[key as keyof typeof ProcessStatusEnum];

              const isTerminatedTab = props.tab === ProviderActionsTabsEnum.Terminated;
              const isTermProviderViewerOnly =
                userContext.roles.includes(Roles.TERM_PROVIDER_VIEWER) &&
                !userContext.roles.includes(Roles.TERM_PROVIDER_ADMIN);

              if (isTerminatedTab && isTermProviderViewerOnly && value === ProcessStatusEnum.Unprocessed) {
                return null;
              }

              return (
                <option key={value} value={value}>
                  {capitalizeFirstLetter(key)}
                </option>
              );
            })}
          </QESSelect>
        </div>

        {props.tabState.filters.processStatus === ProcessStatusEnum.Unprocessed && (
          <QESButton
            disabled={!props.canUndo}
            className="item-column action-buttons"
            id="provider-actions__undo-button"
            qesType="secondary"
            qesTestId="provider-actions__undo-button"
            onClick={props.onUndoClick}
          >
            Undo {props.genericActionText}
          </QESButton>
        )}

        {props.tabState.filters.processStatus === ProcessStatusEnum.Processed && (
          <QESButton
            disabled={!props.canRemove}
            className="item-column action-buttons"
            id="provider-actions__remove-button"
            qesType="secondary"
            qesTestId="provider-actions__remove-button"
            onClick={props.onRemoveClick}
          >
            {props.removeActionText}
          </QESButton>
        )}
      </div>
    </>
  );
};

export default ProviderActionsFilters;